import { Component, EventEmitter, Input, Output, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Firestore, doc, onSnapshot, Unsubscribe } from '@angular/fire/firestore';
import { CoinsingReleaseService } from '../../services/coinsing-release.service';

export type RemoteControlCommandType = 'RESTART_APP' | 'RESTART_PC' | 'UPLOAD_LOGS';
export type CommandStatusState = 'idle' | 'sending' | 'pending' | 'queued' | 'completed' | 'failed';

@Component({
  selector: 'app-konobox-remote-control-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './konobox-remote-control-modal.component.html',
  styleUrls: ['./konobox-remote-control-modal.component.css']
})
export class KonoboxRemoteControlModalComponent implements OnDestroy {
  @Input() deviceId!: string;
  @Input() deviceName!: string;
  @Output() close = new EventEmitter<void>();

  private firestore = inject(Firestore);
  private releaseService = inject(CoinsingReleaseService);

  isSending = false;
  commandStatus = '';
  statusState: CommandStatusState = 'idle';
  logDownloadUrl: string | null = null;
  logFileName: string | null = null;

  // Log Date Selection Dialog
  showDateDialog = false;
  selectedLogDate: string = this.getTodayDateString();

  private statusUnsubscribe?: Unsubscribe;

  ngOnDestroy(): void {
    if (this.statusUnsubscribe) {
      this.statusUnsubscribe();
      this.statusUnsubscribe = undefined;
    }
  }

  getTodayDateString(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getYesterdayDateString(): string {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  setLogDateToday(): void {
    this.selectedLogDate = this.getTodayDateString();
  }

  setLogDateYesterday(): void {
    this.selectedLogDate = this.getYesterdayDateString();
  }

  promptForLogDate(): void {
    this.selectedLogDate = this.getTodayDateString();
    this.showDateDialog = true;
  }

  cancelDatePrompt(): void {
    this.showDateDialog = false;
  }

  confirmDateAndFetchLog(): void {
    if (!this.selectedLogDate) return;
    const date = this.selectedLogDate;
    this.showDateDialog = false;
    void this.sendCommand('UPLOAD_LOGS', date);
  }

  async onCommandClicked(command: RemoteControlCommandType): Promise<void> {
    if (this.isSending) return;

    const name = this.deviceName || this.deviceId;

    if (command === 'UPLOAD_LOGS') {
      this.promptForLogDate();
      return;
    }

    if (command === 'RESTART_APP') {
      const confirmed = window.confirm(
        `Restart the application on ${name}? If a customer is using the kiosk, the restart waits until the kiosk is idle. The kiosk checks for commands every 10 minutes.`
      );
      if (!confirmed) return;
    } else if (command === 'RESTART_PC') {
      const confirmed = window.confirm(
        `Restart the Windows PC on ${name}? The kiosk will be offline for a few minutes. If a customer is using it, the restart waits until the kiosk is idle.`
      );
      if (!confirmed) return;
    }

    await this.sendCommand(command);
  }

  async sendCommand(command: RemoteControlCommandType, logDate?: string): Promise<void> {
    if (!this.deviceId || this.isSending) return;

    if (this.statusUnsubscribe) {
      this.statusUnsubscribe();
      this.statusUnsubscribe = undefined;
    }

    this.isSending = true;
    this.logDownloadUrl = null;
    this.logFileName = null;
    this.statusState = 'sending';
    this.commandStatus = logDate
      ? `Requesting logs for ${logDate}...`
      : 'Sending command...';

    try {
      const res = await this.releaseService.dispatchCommand(this.deviceId, command, logDate);
      const commandId = res.commandId;

      this.statusState = 'pending';
      this.commandStatus = 'Command sent. Waiting for kiosk...';

      // Listen for command completion in coinsing_commands/{commandId}
      const cmdRef = doc(this.firestore, 'coinsing_commands', commandId);
      this.statusUnsubscribe = onSnapshot(cmdRef, (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const status = data['status'];

        if (status === 'queued') {
          this.statusState = 'queued';
          this.commandStatus = 'Command queued. Kiosk is currently in an active customer session and will execute once idle.';
        } else if (status === 'completed') {
          this.statusState = 'completed';
          this.isSending = false;
          if (command === 'UPLOAD_LOGS') {
            this.commandStatus = 'Logs uploaded successfully!';
            if (data['downloadUrl']) {
              this.logDownloadUrl = data['downloadUrl'];
              this.logFileName = data['fileName'] || `${logDate || 'app'}.log`;
              window.open(this.logDownloadUrl!, '_blank');
            }
          } else {
            this.commandStatus = 'Command completed successfully!';
          }
          if (this.statusUnsubscribe) {
            this.statusUnsubscribe();
            this.statusUnsubscribe = undefined;
          }
        } else if (status === 'failed') {
          this.statusState = 'failed';
          this.isSending = false;
          const reason = data['errorMessage'] || data['failureReason'] || 'Unknown error';
          this.commandStatus = `Command failed: ${reason}`;
          if (this.statusUnsubscribe) {
            this.statusUnsubscribe();
            this.statusUnsubscribe = undefined;
          }
        }
      }, (err) => {
        console.error('Error listening to command status:', err);
      });
    } catch (err: any) {
      console.error('Failed to dispatch command:', err);
      this.statusState = 'failed';
      this.commandStatus = `Error: ${err?.message || 'Failed to dispatch command'}`;
      this.isSending = false;
    }
  }
}
